// React
import { useEffect, useState } from 'react';

// UI primitives
import { Toaster } from '@ui/toaster';

// Shared components
import { Page } from '../layouts/Page';
import { PageHeaderTotalLabel, PageHeaderSearchBar } from '../layouts/PageHeader';
import { ResponsivePageHeader } from '../layouts/ResponsivePageHeader';
import { openMobileSidebar } from '../MobileSidebar/mobileSidebarEvents';
import { DataTableFooter } from '@components/DataTable/DataTableFooter';

// Local components
import { AllResaleItemsPage } from '@components/ResaleItem/Page/AllResaleItemsPage';
import { CreateResaleItemDialog } from '@components/ResaleItem/Sections/CreateResaleItemDialog';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog';

// Hooks
import { useResaleItems, useUpdateResaleItemStatus } from '@hooks/resaleItems';
import { useResaleItemsRouterSearch } from '@hooks/useResaleItemsRouterSearch';
import { searchQueryParam } from '@/react/utils/searchQuery';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useTranslation } from '@hooks/useTranslation';

// Lib
import { can, CAPABILITIES, type CurrentUser } from '../../lib/capabilities';

// Constants
import { SEARCH_BAR_WIDTH } from '../../constants';

interface ResaleItemManagementProps {
  currentUser?: CurrentUser;
}

export default function ResaleItemManagement({ currentUser }: ResaleItemManagementProps) {
  const { t } = useTranslation();
  // RBAC: the backend policy is the source of truth; this only hides write
  // affordances (create / edit / activate / deactivate) from read-only users.
  const canWrite = can(currentUser, CAPABILITIES.RESALE_ITEMS_WRITE);
  const router = useResaleItemsRouterSearch();
  const { search } = router;

  const [searchInput, setSearchInput] = useState(search.q);
  const debouncedQuery = useDebouncedValue(searchInput);

  // Re-sync local input when URL changes externally (popstate / in-router nav)
  useEffect(() => {
    setSearchInput(search.q);
  }, [search.q]);

  // Sync debounced typing back to URL (page-1 reset is handled by setQuery)
  useEffect(() => {
    if (debouncedQuery !== search.q) {
      router.setQuery(debouncedQuery);
    }
  }, [debouncedQuery, search.q, router]);

  const { data, isLoading, isFetching, isError, refetch } = useResaleItems({
    q: searchQueryParam(debouncedQuery),
    page: search.page,
    perPage: search.perPage,
    status: search.status,
  });

  const resaleItems = data?.resaleItems ?? [];
  const filteredCount = data?.pagination.total ?? 0;
  const totalUnfiltered = data?.totalUnfiltered ?? 0;
  const totalPages = Math.ceil(filteredCount / search.perPage);

  const updateStatus = useUpdateResaleItemStatus();
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<number | null>(null);

  const handleStatusToggle = (id: number, active: boolean) => {
    // Only deactivate prompts for confirmation — activate is non-destructive.
    if (active) {
      updateStatus.mutate({ id, active: true });
    } else {
      setConfirmDeactivateId(id);
    }
  };

  const handleConfirmDeactivate = () => {
    if (confirmDeactivateId == null) return;
    updateStatus.mutate({ id: confirmDeactivateId, active: false });
    setConfirmDeactivateId(null);
  };

  const [createModalOpen, setCreateModalOpen] = useState(false);

  return (
    <>
      <Toaster />
      <Page>
        <Page.Header className="py-6 md:py-0">
          <ResponsivePageHeader
            mobile={{
              title: t('header.title'),
              count: isLoading ? '…' : filteredCount.toLocaleString(),
              searchValue: searchInput,
              onSearchChange: setSearchInput,
              searchPlaceholder: t('search.placeholder'),
              searchAriaLabel: t('search.aria_label'),
              openNavigationAriaLabel: t('header.open_navigation'),
              openSearchAriaLabel: t('header.open_search'),
              closeSearchAriaLabel: t('header.close_search'),
              onOpenNavigation: openMobileSidebar,
            }}
          >
            <div className="shrink-0">
              <PageHeaderTotalLabel
                count={totalUnfiltered}
                label={t('header.total')}
              />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{ flexBasis: SEARCH_BAR_WIDTH }}
            >
              <PageHeaderSearchBar
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t('search.placeholder')}
                ariaLabel={t('search.aria_label')}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{ flexShrink: 99 }}
            >
              <PageHeaderTotalLabel
                count={totalUnfiltered}
                label={t('header.total')}
              />
            </div>
          </ResponsivePageHeader>
        </Page.Header>
        <Page.Body>
          <AllResaleItemsPage
            currentStatus={search.status}
            totalCount={filteredCount}
            onStatusChange={router.setStatus}
            onCreateNewClick={() => setCreateModalOpen(true)}
            onStatusToggle={handleStatusToggle}
            resaleItems={resaleItems}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onRetry={refetch}
            canWrite={canWrite}
          />
        </Page.Body>
        <Page.Footer>
          <DataTableFooter
            currentPage={search.page}
            totalPages={totalPages}
            totalCount={filteredCount}
            perPage={search.perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            className="flex flex-col justify-center h-full px-6 sm:px-8 lg:px-16"
          />
        </Page.Footer>
      </Page>

      <CreateResaleItemDialog
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />

      <ConfirmationDialog
        open={confirmDeactivateId != null}
        onOpenChange={(open) => { if (!open) setConfirmDeactivateId(null); }}
        onConfirm={handleConfirmDeactivate}
        title={t('status_dropdown.deactivate_confirm_title')}
        message={t('status_dropdown.deactivate_confirm_message')}
        confirmLabel={t('status_dropdown.deactivate_confirm_action')}
        cancelLabel={t('status_dropdown.deactivate_cancel_action')}
        variant="destructive"
      />
    </>
  );
}
